// Pricing — Free vs Studio tiers (PayPal Smart Buttons + Google Pay)
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, Radio, Crown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import PayPalCheckout from "../components/PayPalCheckout";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [openCheckout, setOpenCheckout] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => { api.get("/membership/tiers").then(({ data }) => setTiers(data)); }, []);
  useEffect(() => { if (params.get("upgrade") === "studio" && user?.membership_tier !== "studio") setOpenCheckout(true); }, [params, user]);

  return (
    <div className="max-w-[1500px] mx-auto space-y-12" data-testid="pricing-page">
      <Toaster theme="dark" position="top-center" />
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <div className="label-eyebrow">// membership</div>
        <h1 className="font-display font-black text-4xl lg:text-7xl tracking-tighter">Two tiers.<br /><span className="text-nexus-cyan">One universe.</span></h1>
        <p className="text-white/50">Anyone can <strong className="text-white">sign up free</strong>. Streamers and pros upgrade to <strong className="text-nexus-cyan">Studio ($20/mo)</strong> for the full creator suite — go-live dashboard, BReels AI, custom stickers, 95% revenue share.</p>
        <div className="flex flex-wrap gap-3 justify-center pt-2">
          {!user && (
            <>
              <button onClick={() => nav("/auth?mode=signup")} className="btn-ghost" data-testid="signup-free-btn">Sign up Free →</button>
              <button onClick={() => nav("/auth?mode=signup&tier=studio")} className="btn-nexus" data-testid="signup-studio-btn">Sign up · Studio $20/mo →</button>
            </>
          )}
          {user && user.membership_tier !== "studio" && (
            <button onClick={() => setOpenCheckout(true)} className="btn-nexus" data-testid="open-studio-checkout">Upgrade to Studio · $20/mo</button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {tiers.map((t) => {
          const isStudio = t.id === "studio";
          return (
            <div key={t.id} data-testid={`tier-${t.id}`} className={`relative p-8 lg:p-10 border ${isStudio ? "border-nexus-cyan glass-strong" : "border-white/10 bg-nexus-surface"}`}>
              {isStudio && <div className="absolute -top-3 left-8 bg-nexus-cyan text-nexus-obsidian px-3 py-1 text-[9px] tracking-[0.3em] font-display font-bold uppercase">Recommended</div>}
              <div className="flex items-center gap-3">
                {isStudio ? <Radio className="w-7 h-7 text-nexus-cyan" /> : <Crown className="w-7 h-7 text-white/40" />}
                <div className="font-display font-black text-3xl">{t.name}</div>
              </div>
              <div className="text-[11px] tracking-[0.3em] uppercase mt-2 text-white/50">{t.tagline}</div>
              <div className="mt-6 font-display font-black text-5xl">
                {t.price === 0 ? "FREE" : `$${t.price}`}
                {t.price > 0 && <span className="text-base text-white/40 ml-2">/ month</span>}
              </div>
              <ul className="mt-8 space-y-3">
                {t.perks.map((p, i) => (
                  <li key={i} className="flex gap-3 text-sm text-white/80">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: isStudio ? "#00F0FF" : "rgba(255,255,255,0.5)" }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {t.id === "free" ? (
                <button onClick={() => user ? toast.success("You're on Free") : nav("/auth?mode=signup")} data-testid={`buy-${t.id}`} className="btn-ghost w-full mt-8 justify-center">{user ? "Stay on Free" : "Sign up Free"}</button>
              ) : (
                <button onClick={() => user ? setOpenCheckout(true) : nav("/auth?mode=signup&tier=studio")} data-testid={`buy-${t.id}`} className="btn-nexus w-full mt-8 justify-center">{user?.membership_tier === "studio" ? "Already Studio" : (user ? "Upgrade · $20/mo" : "Sign up · Studio $20/mo")}</button>
              )}
            </div>
          );
        })}
      </div>

      {openCheckout && user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setOpenCheckout(false)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="studio-checkout-modal">
            <h3 className="font-display text-2xl">Activate Studio Tier</h3>
            <p className="text-xs text-white/50">$20/month · cancel anytime · 95% tip revenue share, BReels AI, custom stickers, payout in AUD after KYC.</p>
            <PayPalCheckout purpose="studio_tier" amountUSD={20} refId="studio" label="Activate Studio" onSuccess={() => { refresh(); setOpenCheckout(false); setTimeout(() => nav("/studio"), 600); }} />
          </div>
        </div>
      )}

      <div className="text-center text-[11px] text-white/40 max-w-2xl mx-auto pt-4 border-t border-white/10">
        <strong className="text-white/70">Site subscription</strong> (Studio Tier · $20 to NEXUS) is separate from becoming a <strong className="text-white/70">supporter</strong> ($4.90, one-time, 30 days — money goes to the streamer you support).
      </div>
    </div>
  );
}

